import React, { useEffect, useState } from "react";
import { FaQrcode, FaCopy, FaCheck, FaKey, FaExclamationTriangle } from "react-icons/fa";
import NumberInput from "../../atoms/NumberInput";
import Checkbox from "../../atoms/Checkbox";

/**
 * Manuel kota talebi kartı (makine 3.9.4+, QR) — cihaz bilgi ekranı (#115).
 *
 * Yalnız gösterim + form; veri ve çağrı rol kabuklarında
 * (storageRoles/ / dealerRoles/ ManualQuotaRequestSection).
 * Sözleşme: BayinerIOT-Workspace- docs/api-contracts/manual-quota-qr.md §4
 * Bir QR talebine tek kod (#129): kod üretildiyse yalnız son kod görünür.
 */

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("tr-TR"));
const signed = (n) => (n == null ? "—" : `${Number(n) > 0 ? "+" : ""}${fmt(n)}`);
const dateTime = (d) =>
  d
    ? new Date(d).toLocaleString("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const STATUS = {
  open: { label: "Kod bekliyor", className: "bg-amber-100 text-amber-800" },
  codeGenerated: { label: "Kod üretildi", className: "bg-emerald-100 text-emerald-800" },
};

const MODE = {
  normal: "Normal",
  retry: "Yeniden üretim — önceki kod makineye girilmemişti, firmadan yalnız fark düşüldü",
  pending: "Bekleyen senkron — bekleyen iade mahsubu da uygulandı",
};

const Row = ({ label, value, strong = false }) => (
  <div className="flex items-center justify-between gap-3 py-1 text-sm">
    <span className="text-gray-500">{label}</span>
    <span className={`tabular-nums text-right ${strong ? "font-Bold text-gray-900" : "font-Medium text-gray-700"}`}>
      {value}
    </span>
  </div>
);

// Şüpheli durumlar (#123) — sunucu `warnings[]`.
const warningText = (w) => {
  if (w.code === "counterBackwards") {
    return `Sayaç geriye gitmiş: kayıttaki ${fmt(w.expected)}, makinedeki ${fmt(w.actual)}${
      w.productName ? ` (${w.productName})` : ""
    }. Tablet sıfırlanmış ya da uygulama verisi silinmiş olabilir.`;
  }
  if (w.code === "clockAhead") {
    return `Makine saati ileri: QR saati ${dateTime(w.actual)}, sunucu ${dateTime(w.expected)}. Makinede saat ayarını düzeltin.`;
  }
  return w.code;
};

export default function ManualQuotaRequestCard({
  request,
  onGenerate,
  isGenerating = false,
  error = "",
  canForce = false,
}) {
  const [newQuota, setNewQuota] = useState("");
  const [copied, setCopied] = useState(false);
  const [forceConfirmed, setForceConfirmed] = useState(false);
  const warnings = request.warnings || [];
  const counterBackwards = warnings.some((w) => w.code === "counterBackwards");
  // Sayaç geriye giden talepte kod yalnız merkez onayıyla (#123).
  const needsForce = counterBackwards;
  const blockedForDealer = needsForce && !canForce;

  const machine = request.products.find((p) => p.productName === request.lockedProduct) || {};
  const status = STATUS[request.status] || STATUS.open;
  const last = request.lastCode;

  // Yeni talep gelince form sıfırlanır.
  useEffect(() => {
    setNewQuota("");
    setForceConfirmed(false);
  }, [request.id]);

  const quota = newQuota === "" ? null : Number(newQuota);
  const invalid =
    quota == null || !Number.isInteger(quota) || quota < 0 || quota === machine.remaining ||
    blockedForDealer || (needsForce && !forceConfirmed);
  const hint =
    quota != null && quota === machine.remaining
      ? "Makinedeki kalan kotayla aynı olamaz."
      : quota != null && !Number.isInteger(quota)
        ? "Tam sayı girin."
        : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(last.code));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-white shadow-sm overflow-hidden">
      {/* Başlık */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-100 bg-amber-50/60 px-4 py-2.5">
        <div className="flex items-center gap-2.5">
          <div className="rounded-lg bg-amber-500 p-1.5 text-white shadow-sm">
            <FaQrcode className="h-3.5 w-3.5" />
          </div>
          <div>
            <span className="block text-sm font-SemiBold text-gray-900">Manuel Kota Talebi</span>
            <span className="block text-xs text-gray-500">
              QR: {dateTime(request.createdAt)} · {request.lockedProduct} · v{request.appVersion}
            </span>
          </div>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-Bold ${status.className}`}>{status.label}</span>
      </div>

      {warnings.length > 0 && (
        <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          {warnings.map((w, i) => (
            <p key={`${w.code}-${i}`} className="flex items-start gap-2 py-0.5 text-sm text-red-700">
              <FaExclamationTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{warningText(w)}</span>
            </p>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-3">
        {/* Makine değerleri */}
        <div>
          <p className="mb-1 text-xs font-Bold uppercase tracking-wide text-gray-400">Makineden okunan</p>
          {request.products.map((p) => (
            <div
              key={p.productName}
              className={`mb-2 rounded-lg border px-3 py-2 ${
                p.productName === request.lockedProduct ? "border-amber-200 bg-amber-50/40" : "border-gray-100"
              }`}
            >
              <p className="text-sm font-SemiBold text-gray-800">{p.productName}</p>
              <Row label="Makinedeki kalan" value={fmt(p.remaining)} strong />
              <Row label="Paneldeki kalan" value={fmt(p.panelQuota)} />
              <Row label="Toplam sayaç" value={fmt(p.counter)} />
            </div>
          ))}
        </div>

        {/* Tüketim kayıtları */}
        <div>
          <p className="mb-1 text-xs font-Bold uppercase tracking-wide text-gray-400">Tüketim kayıtları</p>
          <div className="rounded-lg border border-gray-100 px-3 py-2">
            <Row label="Kayıt sayısı" value={fmt(request.logs?.count)} strong />
            <Row label="İlk kayıt" value={dateTime(request.logs?.from)} />
            <Row label="Son kayıt" value={dateTime(request.logs?.to)} />
            {(request.logs?.byProduct || []).map((b) => (
              <Row key={b.productName} label={`${b.productName} tüketimi`} value={fmt(b.consument)} />
            ))}
            {request.logs?.summaryCount > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                {fmt(request.logs.summaryCount)} kayıt günlük özet olarak geldi (QR'a sığdırmak için).
              </p>
            )}
            {!request.logs?.count && (
              <p className="mt-1 text-xs text-gray-500">Son senkrondan bu yana tüketim yok.</p>
            )}
          </div>
        </div>

        {/* Kod */}
        <div>
          <p className="mb-1 text-xs font-Bold uppercase tracking-wide text-gray-400">
            {last ? "Son kod" : "Kod üret"}
          </p>
          {last && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-lg font-Bold tracking-wider text-gray-900 break-all">
                  {last.code}
                </span>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-emerald-100 hover:text-emerald-700"
                  title="Kopyala"
                >
                  {copied ? <FaCheck className="h-3.5 w-3.5" /> : <FaCopy className="h-3.5 w-3.5" />}
                </button>
              </div>
              <p className="mb-1 text-xs text-gray-500">Makinedeki "Kod Girin" alanına girilecek.</p>
              <Row label="Yeni kalan kota" value={fmt(last.newQuota)} strong />
              <Row label="Firmadan düşülen" value={signed(last.firmCharge)} />
              <Row label="Çevrimdışı tüketim" value={fmt(last.offlineConsumption)} />
              <Row label="Firma havuzu" value={`${fmt(last.firmQuotaBefore)} → ${fmt(last.firmQuotaAfter)}`} />
              <Row label="Üreten" value={`${last.by || "—"} · ${dateTime(last.at)}`} />
              <p className="mt-1 text-xs text-gray-500">{MODE[last.mode] || last.mode}</p>
              {last.forced && (
                <p className="mt-1 text-xs font-Medium text-red-600">Merkez onayıyla üretildi (sayaç geriye gitmişti).</p>
              )}
              <p className="mt-2 text-xs text-gray-500">
                Bu QR için kod üretildi; ikinci kod üretilemez. Makinede yeni QR oluşunca yeni talep gelir.
              </p>
            </div>
          )}

          {!last && (
            <>
              <NumberInput
                label={<span className="text-sm font-Medium text-gray-600">Yeni kalan kota</span>}
                value={newQuota}
                onChange={setNewQuota}
                placeholder="Makinede olması gereken kalan kota"
                min={0}
                error={!!hint}
                errorMessage={hint}
              />
              {quota != null && !invalid && (
                <p className="mt-1 text-xs text-gray-500">
                  Makinede {signed(quota - machine.remaining)} değişim.
                </p>
              )}
              {blockedForDealer && (
                <p className="mt-2 text-xs font-Medium text-red-600">
                  Sayaç geriye gittiği için kod yalnız merkez onayıyla üretilebilir. Merkezle iletişime geçin.
                </p>
              )}
              {needsForce && canForce && (
                <div className="mt-2">
                  <Checkbox
                    label="Merkez onayıyla yine de üret"
                    checked={forceConfirmed}
                    onChange={(e) => setForceConfirmed(e.target.checked)}
                  />
                </div>
              )}
              {error && <p className="mt-2 text-xs font-Medium text-red-600">{error}</p>}
              <button
                type="button"
                onClick={() => onGenerate(quota, needsForce && forceConfirmed)}
                disabled={invalid || isGenerating}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-SemiBold text-white shadow-sm transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isGenerating ? (
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <FaKey className="h-3.5 w-3.5" />
                )}
                Kod üret
              </button>
            </>
          )}
          {last && error && <p className="mt-2 text-xs font-Medium text-red-600">{error}</p>}
        </div>
      </div>
    </div>
  );
}
